import { ConfirmDialog } from "@/components/reusable";
import { AddRuleModal } from "@/components/schema-builder/AddRuleModal";
import { RuleConfigurationModal } from "@/components/schema-builder/RuleConfigurationModal";
import { SchemaRuleCard } from "@/components/schema-builder/SchemaRuleCard";
import { getRuleTemplates } from "@/services/schemaBuilderApi";
import {
  buildMandatoryRule,
  buildNoDuplicatesRule,
  columnIdsByName,
  getDefaultExampleForDataType,
  getDefaultFormattingForDataType,
  isAutoTypeCheckRule,
  isDuplicatesRule,
  isMandatoryRule,
  reconcileDataTypeCheckRules,
  toColumnOptions,
} from "@/lib/schemaBuilderHelpers";
import { SCHEMA_DATA_TYPES } from "@/types/schemaBuilder";
import type {
  SchemaColumn,
  SchemaRule,
  SchemaRuleCategory,
  SchemaRulePreset,
} from "@/types/schemaBuilder";
import { Trash2, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";

interface ColumnEditorDrawerProps {
  column: SchemaColumn;
  totalColumns: number;
  allColumns?: SchemaColumn[];
  mode?: "edit" | "create";
  rules: SchemaRule[];
  referenceLists: { id: string; name: string }[];
  onAddRule: (rule: SchemaRule) => void;
  onUpdateRule: (rule: SchemaRule) => void;
  onDeleteRule: (rule: SchemaRule) => void;
  onUpdateColumn: (fieldId: string, updates: Partial<SchemaColumn>) => void;
  onCreateColumn?: (column: SchemaColumn) => void;
  onCreateReferenceList: (name: string, values: string[]) => string;
  onDeleteColumn: (fieldId: string) => void;
  onClose: () => void;
  onFileUploaded?: (fileName: string) => void;
}

const TABS = ["Basics", "Rules"];

export function ColumnEditorDrawer({
  column,
  totalColumns,
  allColumns = [],
  mode = "edit",
  rules,
  referenceLists,
  onAddRule,
  onUpdateRule,
  onDeleteRule,
  onUpdateColumn,
  onCreateColumn,
  onCreateReferenceList,
  onDeleteColumn,
  onClose,
  onFileUploaded,
}: ColumnEditorDrawerProps) {
  const isCreateMode = mode === "create";

  const [activeTab, setActiveTab] = useState("Basics");
  const [sortOrder, setSortOrder] = useState(column.sortOrder);
  const [columnName, setColumnName] = useState(
    isCreateMode ? column.name || `Column_${column.sortOrder}` : column.name
  );
  const [dataType, setDataType] = useState<SchemaColumn["dataType"] | "">(
    isCreateMode ? "" : column.dataType
  );
  const [description, setDescription] = useState(column.description ?? "");

  const columnRules = rules.filter(
    (rule) => rule.columnIds.includes(column.fieldId) && (rule.scope ?? "column") === "column"
  );

  // Detects in-session rule changes so Save Changes enables even when only the Rules tab changed.
  const initialColumnRulesSnapshotRef = useRef(JSON.stringify(columnRules));
  const rulesChangedThisSession =
    JSON.stringify(columnRules) !== initialColumnRulesSnapshotRef.current;

  const [mandatory, setMandatory] = useState(column.mandatory || columnRules.some(isMandatoryRule));
  const [unique, setUnique] = useState(column.unique || columnRules.some(isDuplicatesRule));

  const [showDeleteColumnDialog, setShowDeleteColumnDialog] = useState(false);
  const [showAddRuleModal, setShowAddRuleModal] = useState(false);
  const [selectedPreset, setSelectedPreset] = useState<SchemaRulePreset | null>(null);
  const [lastRuleCategory, setLastRuleCategory] = useState<SchemaRuleCategory>("All");
  const [ruleToEdit, setRuleToEdit] = useState<SchemaRule | null>(null);
  const [ruleToDelete, setRuleToDelete] = useState<SchemaRule | null>(null);

  // Needed to re-resolve the real preset (with its expression template) when editing an
  // existing rule -- see getPresetForRule below.
  const [allPresets, setAllPresets] = useState<SchemaRulePreset[]>([]);
  useEffect(() => {
    void getRuleTemplates()
      .then(setAllPresets)
      .catch(() => {});
  }, []);

  const [formatting, setFormatting] = useState(() => column.formatting ?? "---");

  // Live dataType state so create-mode reflects the already-selected type before first save.
  const effectiveDataType = dataType !== "" ? dataType : column.dataType;

  // Live preview of the Rules tab post-save, without committing to parent state yet.
  const previewRules = (() => {
    let result = [...columnRules];
    const hasMandatory = result.some(isMandatoryRule);
    const hasDuplicates = result.some(isDuplicatesRule);
    const name = columnName || column.name;

    if (mandatory && !hasMandatory) {
      result = [buildMandatoryRule(column.fieldId, name, "__preview_mandatory__"), ...result];
    } else if (!mandatory && hasMandatory) {
      result = result.filter((r) => !isMandatoryRule(r));
    }

    if (unique && !hasDuplicates) {
      result = [...result, buildNoDuplicatesRule(column.fieldId, name, "__preview_duplicates__")];
    } else if (!unique && hasDuplicates) {
      result = result.filter((r) => !isDuplicatesRule(r));
    }

    // valid_number/valid_date preview; the backend creates the real rule on save.
    result =
      dataType === ""
        ? result.filter((r) => !isAutoTypeCheckRule(r))
        : reconcileDataTypeCheckRules(
            [{ fieldId: column.fieldId, name, dataType: effectiveDataType, formatting }],
            result
          );

    return result;
  })();

  const dateColumnOptions = toColumnOptions(
    allColumns.filter((c) => c.dataType === "Date & Time" && c.fieldId !== column.fieldId)
  );

  // All columns (any type) — for parameters like y_column that accept any data type
  const allColumnOptions = [
    ...(allColumns.some((c) => c.fieldId === column.fieldId)
      ? []
      : columnName || column.name
        ? [{ name: columnName || column.name, fieldId: column.fieldId }]
        : []),
    ...toColumnOptions(allColumns),
  ];

  const alreadySaved = allColumns.some((c) => c.fieldId === column.fieldId);
  const sameTypeColumnOptions = [
    // If the column hasn't been saved yet, inject it so it appears in dropdowns.
    ...(alreadySaved || !effectiveDataType
      ? []
      : [{ name: columnName || column.name, fieldId: column.fieldId }]),
    ...toColumnOptions(allColumns.filter((c) => c.dataType === effectiveDataType)),
  ];

  const numericColumnOptions = toColumnOptions(
    allColumns.filter((c) => c.dataType === "Integer" || c.dataType === "Decimal")
  );

  const columnsByName = columnIdsByName(allColumns);

  const hasChanges =
    isCreateMode ||
    sortOrder !== column.sortOrder ||
    columnName !== column.name ||
    description !== (column.description ?? "") ||
    mandatory !== column.mandatory ||
    unique !== column.unique ||
    dataType !== column.dataType ||
    formatting !== (column.formatting ?? "---") ||
    rulesChangedThisSession;

  const canSave = columnName.trim().length > 0 && dataType !== "" && hasChanges;

  function handleSelectRulePreset(preset: SchemaRulePreset, category: SchemaRuleCategory) {
    setLastRuleCategory(category);
    setShowAddRuleModal(false);
    setSelectedPreset(preset);
  }

  // Falls back to a synthesized preset using the rule's description, never its formula --
  // that's already-substituted display text, not a reusable template.
  function getPresetForRule(rule: SchemaRule): SchemaRulePreset {
    const preset =
      allPresets.find((item) => item.id === rule.presetId) ??
      allPresets.find((item) => item.backendRuleType === rule.backendRuleType) ??
      allPresets.find((item) => item.title === rule.title);

    if (preset) return preset;

    return {
      id: rule.presetId ?? rule.id,
      backendRuleType: rule.backendRuleType ?? rule.id,
      dqType: rule.dqType ?? "Conditional",
      title: rule.title,
      category: rule.category,
      description: rule.description,
      applicableDataTypes: [],
    };
  }

  function handleSaveRule(rule: SchemaRule) {
    // Syncs the flag immediately since the rule itself is already committed.
    if (isMandatoryRule(rule)) {
      setMandatory(true);
      if (!isCreateMode) onUpdateColumn(column.fieldId, { mandatory: true });
    }
    if (isDuplicatesRule(rule)) {
      setUnique(true);
      if (!isCreateMode) onUpdateColumn(column.fieldId, { unique: true });
    }
    onAddRule(rule);
    setSelectedPreset(null);
    setActiveTab("Rules");
  }

  function handleConfirmDeleteRule() {
    if (!ruleToDelete) return;
    if (isAutoTypeCheckRule(ruleToDelete)) {
      setRuleToDelete(null);
      return;
    }

    if (isMandatoryRule(ruleToDelete)) {
      setMandatory(false);
      if (!isCreateMode) onUpdateColumn(column.fieldId, { mandatory: false });
    }
    if (isDuplicatesRule(ruleToDelete)) {
      setUnique(false);
      if (!isCreateMode) onUpdateColumn(column.fieldId, { unique: false });
    }
    if (!ruleToDelete.id.startsWith("__preview_")) onDeleteRule(ruleToDelete);
    setRuleToDelete(null);
  }

  function handleSaveChanges() {
    if (!canSave) return;

    const savedDataType = dataType as SchemaColumn["dataType"];
    const trimmedName = columnName.trim();

    const savedColumn: SchemaColumn = {
      ...column,
      sortOrder,
      name: trimmedName,
      description,
      dataType: savedDataType,
      mandatory,
      unique,
      formatting,
      example: getDefaultExampleForDataType(dataType, column.example),
    };

    const existingMandatoryRule = columnRules.find(isMandatoryRule);
    if (mandatory && !existingMandatoryRule) {
      onAddRule(buildMandatoryRule(column.fieldId, trimmedName));
    } else if (!mandatory && existingMandatoryRule) {
      onDeleteRule(existingMandatoryRule);
    }

    const existingDuplicatesRule = columnRules.find(isDuplicatesRule);
    if (unique && !existingDuplicatesRule) {
      onAddRule(buildNoDuplicatesRule(column.fieldId, trimmedName));
    } else if (!unique && existingDuplicatesRule) {
      onDeleteRule(existingDuplicatesRule);
    }

    if (isCreateMode) {
      onCreateColumn?.(savedColumn);
    } else {
      onUpdateColumn(column.fieldId, savedColumn);
    }

    onClose();
  }

  function handleConfirmDeleteColumn() {
    onDeleteColumn(column.fieldId);
    setShowDeleteColumnDialog(false);
    onClose();
  }

  return (
    <div className="fixed inset-0 z-50 flex justify-end bg-black/40">
      <aside className="flex h-full w-[380px] shrink-0 flex-col bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-[var(--color-border-light)] px-5 py-4">
          <h2 className="text-sm font-semibold text-[var(--color-text-primary)]">Column Editor</h2>

          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-[var(--color-text-secondary)] hover:bg-[var(--color-bg-secondary)]"
          >
            <X className="h-4 w-4" />
          </button>
        </div>

        <div className="border-b border-[var(--color-border-light)] px-5 py-4">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 flex-1 overflow-hidden">
              <div
                className="truncate text-xs font-semibold text-[var(--color-text-primary)]"
                title={columnName || `Column_${sortOrder}`}
              >
                {columnName || `Column_${sortOrder}`}
              </div>

              {!isCreateMode && (
                <div
                  className="mt-0.5 truncate text-xs text-[var(--color-text-tertiary)]"
                  title={column.example ? `Ex. ${column.example}` : undefined}
                >
                  Ex. {column.example}
                </div>
              )}
            </div>

            {!isCreateMode && (
              <button
                type="button"
                onClick={() => setShowDeleteColumnDialog(true)}
                className="shrink-0 rounded border border-red-300 p-1.5 text-red-500 transition hover:bg-red-50"
              >
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            )}
          </div>

          <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2">
            <label className="flex items-center gap-1.5 text-xs text-[var(--color-text-primary)]">
              <input
                type="checkbox"
                checked={mandatory}
                onChange={() => setMandatory((prev) => !prev)}
                className="h-3.5 w-3.5 cursor-pointer accent-[var(--color-action-primary)]"
              />
              Mandatory
            </label>

            <label className="flex items-center gap-1.5 text-xs text-[var(--color-text-primary)]">
              <input
                type="checkbox"
                checked={unique}
                onChange={() => setUnique((prev) => !prev)}
                className="h-3.5 w-3.5 cursor-pointer accent-[var(--color-action-primary)]"
              />
              Unique
            </label>
          </div>
        </div>

        <div className="flex gap-6 border-b border-[var(--color-border-light)] px-5">
          {TABS.map((tab) => {
            const disabled = tab === "Rules" && dataType === "";

            return (
              <button
                key={tab}
                type="button"
                disabled={disabled}
                onClick={() => {
                  if (!disabled) setActiveTab(tab);
                }}
                className={`py-3 text-xs ${
                  activeTab === tab
                    ? "border-b-2 border-[var(--color-brand-primary)] text-[var(--color-text-primary)]"
                    : "text-[var(--color-text-secondary)]"
                } ${disabled ? "cursor-not-allowed opacity-50" : ""}`}
              >
                {tab}
              </button>
            );
          })}
        </div>

        <div className="flex-1 overflow-y-auto px-5 py-5">
          {activeTab === "Basics" && (
            <div className="space-y-4">
              <div className="grid grid-cols-[80px_1fr] gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold">Sort #</label>
                  <select
                    value={sortOrder}
                    onChange={(event) => setSortOrder(Number(event.target.value))}
                    className="h-9 w-full rounded border border-[var(--color-border)] px-2 text-xs"
                  >
                    {Array.from({ length: totalColumns }, (_, i) => i + 1).map((num) => (
                      <option key={num} value={num}>
                        {num}
                      </option>
                    ))}
                  </select>
                </div>

                <div>
                  <label className="mb-1 block text-xs font-semibold">Column Name</label>
                  <input
                    value={columnName}
                    onChange={(event) => setColumnName(event.target.value)}
                    placeholder={`Column_${sortOrder}`}
                    className="h-9 w-full rounded border border-[var(--color-border)] px-2 text-xs"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="mb-1 block text-xs font-semibold">Data Type</label>
                  <select
                    value={dataType}
                    onChange={(event) => {
                      const nextType = event.target.value as SchemaColumn["dataType"] | "";
                      setDataType(nextType);
                      setFormatting(getDefaultFormattingForDataType(nextType));
                    }}
                    className="h-9 w-full rounded border border-[var(--color-border)] px-2 text-xs"
                  >
                    <option value="" disabled>
                      Select Data Type
                    </option>
                    {SCHEMA_DATA_TYPES.map((type) => (
                      <option key={type} value={type}>
                        {type}
                      </option>
                    ))}
                  </select>
                </div>

                <FormattingSelect
                  disabled={dataType === ""}
                  value={formatting}
                  onChange={setFormatting}
                  options={
                    dataType === "Boolean"
                      ? ["1, 0", "Yes, No", "Y, N", "True, False"]
                      : dataType === "Date & Time"
                        ? [
                            "01/03/2024",
                            "01 January, 2024",
                            "01:37 PM",
                            "13:37 PM",
                            "01/03/2024 01:37 PM",
                            "01/03/2024 13:37",
                          ]
                        : ["---"]
                  }
                />
              </div>

              <div>
                <label className="mb-1 block text-xs font-semibold">Description (optional)</label>
                <textarea
                  value={description}
                  onChange={(event) => setDescription(event.target.value)}
                  placeholder="Input your description here"
                  className="h-24 w-full resize-none rounded border border-[var(--color-border)] px-3 py-2 text-xs outline-none focus:border-[var(--color-action-primary)]"
                />
              </div>
            </div>
          )}

          {activeTab === "Rules" && (
            <div>
              <div className="mb-4 flex items-center justify-between">
                <span className="text-xs text-[var(--color-text-primary)]">
                  Rules in this column ({previewRules.length})
                </span>

                <button
                  type="button"
                  onClick={() => setShowAddRuleModal(true)}
                  className="rounded bg-[var(--color-action-primary)] px-3 py-1.5 text-xs font-semibold text-white hover:bg-[var(--color-action-primary-hover)]"
                >
                  Add Rule
                </button>
              </div>

              {previewRules.length > 0 ? (
                <div className="space-y-4">
                  {previewRules.map((rule) => (
                    <SchemaRuleCard
                      key={rule.id}
                      rule={rule}
                      compact
                      isLocked={isAutoTypeCheckRule(rule)}
                      onEdit={
                        rule.id.startsWith("__preview_") || isAutoTypeCheckRule(rule)
                          ? undefined
                          : setRuleToEdit
                      }
                      onDelete={() => setRuleToDelete(rule)}
                    />
                  ))}
                </div>
              ) : (
                <div className="mt-28 text-center text-xs text-[var(--color-text-secondary)]">
                  You currently have no rules configured for this column.
                </div>
              )}
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-[var(--color-border-light)] px-5 py-4">
          <button
            type="button"
            onClick={onClose}
            className="rounded border border-[var(--color-border)] px-4 py-2 text-xs text-[var(--color-text-primary)]"
          >
            Discard Changes
          </button>

          <button
            type="button"
            onClick={handleSaveChanges}
            disabled={!canSave}
            className="rounded bg-[var(--color-action-primary)] px-4 py-2 text-xs font-semibold text-white hover:bg-[var(--color-action-primary-hover)] disabled:bg-[var(--color-bg-secondary)] disabled:text-[var(--color-text-tertiary)]"
          >
            Save Changes
          </button>
        </div>
      </aside>

      {showAddRuleModal && dataType !== "" && (
        <AddRuleModal
          column={{
            fieldId: column.fieldId,
            id: column.id,
            name: columnName,
            dataType,
            example: column.example,
          }}
          existingRuleTypes={
            new Set(previewRules.map((r) => r.backendRuleType).filter(Boolean) as string[])
          }
          initialCategory={lastRuleCategory}
          scopeFilter="column"
          onClose={() => setShowAddRuleModal(false)}
          onSelectPreset={handleSelectRulePreset}
        />
      )}

      {selectedPreset && dataType !== "" && (
        <RuleConfigurationModal
          column={{
            fieldId: column.fieldId,
            id: column.id,
            name: columnName,
            dataType,
            example: column.example,
          }}
          preset={selectedPreset}
          referenceListOptions={referenceLists}
          onCreateReferenceList={onCreateReferenceList}
          onFileUploaded={onFileUploaded}
          dateColumnOptions={dateColumnOptions}
          columnOptions={sameTypeColumnOptions}
          allColumnOptions={allColumnOptions}
          numericColumnOptions={numericColumnOptions}
          columnsByName={columnsByName}
          existingRules={rules}
          onBack={() => {
            setSelectedPreset(null);
            setShowAddRuleModal(true);
          }}
          onClose={() => setSelectedPreset(null)}
          onSave={handleSaveRule}
        />
      )}

      {ruleToEdit && dataType !== "" && (
        <RuleConfigurationModal
          isEditing
          existingRule={ruleToEdit}
          column={{
            fieldId: column.fieldId,
            id: column.id,
            name: columnName,
            dataType,
            example: column.example,
          }}
          preset={getPresetForRule(ruleToEdit)}
          referenceListOptions={referenceLists}
          onCreateReferenceList={onCreateReferenceList}
          onFileUploaded={onFileUploaded}
          dateColumnOptions={dateColumnOptions}
          columnOptions={sameTypeColumnOptions}
          allColumnOptions={allColumnOptions}
          numericColumnOptions={numericColumnOptions}
          columnsByName={columnsByName}
          existingRules={rules}
          onBack={() => setRuleToEdit(null)}
          onClose={() => setRuleToEdit(null)}
          onSave={(updatedRule) => {
            onUpdateRule({
              ...ruleToEdit,
              ...updatedRule,
              id: ruleToEdit.id,
            });
            setRuleToEdit(null);
          }}
        />
      )}

      <ConfirmDialog
        open={showDeleteColumnDialog}
        onOpenChange={setShowDeleteColumnDialog}
        title="Delete column?"
        description={
          <>
            This will remove{" "}
            <span className="font-semibold text-[var(--color-text-primary)]">
              {column.name || "this column"}
            </span>{" "}
            from this schema. Rules only linked to this column will be deleted. Rules linked to
            other columns will be marked as conflicts.
          </>
        }
        confirmLabel="Delete"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={handleConfirmDeleteColumn}
      />

      <ConfirmDialog
        open={Boolean(ruleToDelete)}
        onOpenChange={(open) => {
          if (!open) setRuleToDelete(null);
        }}
        title="Deleting a Rule"
        description={
          <>
            Are you sure you would like to delete{" "}
            <span className="font-semibold text-[var(--color-text-primary)]">
              {ruleToDelete?.ruleKey}
            </span>
            ? Deleting a rule for one column may affect the values of other columns in your schema.
          </>
        }
        confirmLabel="Confirm"
        cancelLabel="Cancel"
        tone="danger"
        onConfirm={handleConfirmDeleteRule}
      />
    </div>
  );
}

function FormattingSelect({
  options,
  value,
  onChange,
  disabled = false,
}: {
  options: string[];
  value: string;
  onChange: (value: string) => void;
  disabled?: boolean;
}) {
  return (
    <div>
      <label className="mb-1 block text-xs font-semibold">Formatting</label>
      <select
        disabled={disabled}
        value={disabled ? "---" : value}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded border border-[var(--color-border)] px-2 text-xs disabled:bg-[var(--color-bg-secondary)] disabled:text-[var(--color-text-tertiary)]"
      >
        {disabled && <option value="---">---</option>}
        {!disabled && options.map((option) => <option key={option}>{option}</option>)}
      </select>
    </div>
  );
}
